import { EnvelopeSimple, PaperPlaneTilt } from '@phosphor-icons/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';

import { Button } from '../components/ui/Button';
import { FieldGroup } from '../components/ui/FieldGroup';
import { Input } from '../components/ui/Input';
import { cn } from '../utils/cn';
import pt from '../locales/pt';

const pagePad =
  'px-space-5 py-space-12 mid:px-space-10 mid:py-space-16 wide:px-space-20 wide:py-space-24';

const schema = z.object({
  name: z.string().min(1, pt.contact.errors.name),
  email: z.string().min(1, pt.contact.errors.email).email(pt.contact.errors.email),
  message: z.string().min(1, pt.contact.errors.message),
});

/**
 * Contact · 375/768/1280 × light/dark — evplanner-gleb-design-system.pen
 *
 * Sem endpoint de backend para contacto — submissão abre um mailto: para
 * apoio@evplanner.eu com os dados preenchidos.
 */
export function Contact() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { name: '', email: '', message: '' } });

  const onSubmit = ({ name, email, message }) => {
    const subject = encodeURIComponent(`Contacto de ${name} (EvPlanner)`);
    const body = encodeURIComponent(`${message}\n\n${name} (${email})`);
    toast(pt.contact.toastOpening);
    window.location.href = `mailto:${pt.contact.supportEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <section className={`flex flex-col items-center ${pagePad}`}>
      <div className="flex w-full max-w-2xl flex-col gap-space-10">
        <div className="flex flex-col gap-space-3">
          <span className="type-section text-sage-700 dark:text-sage-300">{pt.contact.eyebrow}</span>
          <h1 className="type-landing-h2">{pt.contact.title}</h1>
          <p className="type-landing-lead">{pt.contact.lead}</p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="flex flex-col gap-space-5 rounded-ds-lg border border-border bg-card p-space-8"
        >
          <FieldGroup id="contact-name" label={pt.contact.form.name.label} error={errors.name?.message}>
            <Input
              id="contact-name"
              type="text"
              autoComplete="name"
              invalid={!!errors.name}
              placeholder={pt.contact.form.name.placeholder}
              {...register('name')}
            />
          </FieldGroup>
          <FieldGroup id="contact-email" label={pt.contact.form.email.label} error={errors.email?.message}>
            <Input
              id="contact-email"
              type="email"
              autoComplete="email"
              invalid={!!errors.email}
              placeholder={pt.contact.form.email.placeholder}
              {...register('email')}
            />
          </FieldGroup>
          <FieldGroup id="contact-message" label={pt.contact.form.message.label} error={errors.message?.message}>
            <textarea
              id="contact-message"
              rows={5}
              aria-invalid={!!errors.message || undefined}
              placeholder={pt.contact.form.message.placeholder}
              className={cn(
                'flex w-full resize-none rounded-ds-md border border-border bg-card px-space-4 py-space-3 type-body text-foreground placeholder:text-stone-400 focus:border-sage-500 focus:outline-none focus:ring-4 focus:ring-sage-glow-soft disabled:cursor-not-allowed disabled:opacity-50',
                errors.message && 'border-danger-500',
              )}
              {...register('message')}
            />
          </FieldGroup>
          <Button type="submit" size="lg" className="w-full gap-space-2">
            {pt.contact.form.submit}
            <PaperPlaneTilt className="h-5 w-5" aria-hidden />
          </Button>
        </form>

        <div className="flex items-center justify-center gap-space-2 type-body font-medium text-muted-foreground">
          <EnvelopeSimple className="h-4 w-4 text-sage-700 dark:text-sage-300" aria-hidden />
          <span>{pt.contact.altEmail}</span>
        </div>
      </div>
    </section>
  );
}
